// Tracks which object the player has "marked" as a Transfer source -- the answer
// to "how do you pick two objects, neither of which is the caster, for a spell
// that normally only knows caster<->target". Tap a linkable object (one with
// mutable properties, see GameScene's ObjectDef.properties), mark it, then cast
// Transfer on a different object; ui.ts's performCast reads this to know which
// object plays the "from" role. In-memory only, like doors.ts -- reset on New Game.

// The mark itself lives in the shared WorldState as of 2026-07-30 (sim
// extraction step 2). Behavior is deliberately unchanged: it is still a bare
// id/name pair with no room attached, so a mark carried into another room still
// falls back to the caster at cast time (see GameScene's applyTransferEffect and
// puzzle.spec.ts:318). Room-qualifying it is a real improvement and a real
// behavior change, so it gets its own commit.
//
// The listener list stays here rather than in WorldState -- it is UI wiring (the
// scene redraws its reticle), not world data, and the sim has no business
// knowing that anything is watching.
import { getWorld } from './activeWorld';
// Marking is a `SimCommand` like any other -- see moveRecorder.ts.
import { recordCommand } from './moveRecorder';

const listeners: Array<() => void> = [];

// GameScene has no reference to this module's own state and listens for changes
// to draw a targeting reticle over the marked object (see refreshMarkIndicator),
// the same one-directional pub/sub shape as onKeybindingsChange/onDebugOverlayChange.
export function onLinkedSourceChange(listener: () => void): void {
  listeners.push(listener);
}

export function setLinkedSource(id: string, name: string): void {
  recordCommand({ kind: 'mark', id });
  getWorld().markedSource = { id, name };
  listeners.forEach((listener) => listener());
}

export function getLinkedSource(): string | null {
  return getWorld().markedSource?.id ?? null;
}

// Reads the marked object's name **live** rather than returning the snapshot
// taken when it was marked. The id is the mark's identity; the name is only
// presentation, and presentation can now change underneath it -- a relic
// (sim/relic.ts) is renamed by its own Entropy the moment a cast moves any, so
// a marked Withered Standard drained below the threshold really is a Pristine
// Standard afterward. Showing the name it had at mark time is the same class of
// staleness that ladder exists to fix, one layer up in the HUD.
//
// Falls back to the stored name when the object is not in the current room --
// a mark carried through a door still labels itself sensibly, and still falls
// back to the caster at cast time exactly as this module's header describes.
export function getLinkedSourceName(): string | null {
  const marked = getWorld().markedSource;
  if (!marked) return null;
  const world = getWorld();
  const room = world.rooms[world.player.roomId];
  return room?.objects.find((o) => o.id === marked.id)?.name ?? marked.name;
}

export function clearLinkedSource(): void {
  recordCommand({ kind: 'mark', id: null });
  getWorld().markedSource = null;
  listeners.forEach((listener) => listener());
}
